import axios from 'axios'
import { ElLoading, ElMessage } from 'element-plus'
import { isLogin } from '@/utils/auth'
// import qs from 'qs'

// 请求重试配置
const MAX_RETRY_COUNT = 3 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟时间（毫秒）

let loadingService: any
let reqNum = 0
// 延迟关闭 loading 的定时器
let closeLoadingTimer: any = null

// 递归移除对象中值为 null 的字段
function removeNullFields(obj: any) {
  if (obj && typeof obj === 'object') {
    Object.keys(obj).forEach((key) => {
      if (obj[key] === null) {
        delete obj[key]
      }
      else if (typeof obj[key] === 'object') {
        removeNullFields(obj[key]) // 递归处理嵌套对象
      }
    })
  }
  return obj
}

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  export interface AxiosInstance {
    /** 流式请求方法 */
    stream: any
    /** 基础 URL */
    baseUrl: string
    /** 上传文件方法 */
    uploadFile: any
  }
  export interface AxiosRequestConfig {
    retry?: boolean
    retryCount?: number
    /**
     * 是否根据角色请求
     * @default false
     */
    isForRole?: boolean
    /**
     * 是否返回所有数据，包含请求头等信息
     * @default false
     */
    isResponseAll?: boolean
    /**
     * 是否跳过 loading 动画
     * @default false
     */
    noLoading?: boolean
    /**
     * 是否跳过错误提示
     * @default false
     */
    noErrorTip?: boolean
    /**
     * 内部标记：是否为重试请求（用于避免重复计数）
     * @internal
     */
    _isRetry?: boolean
  }
}

export const baseURL = (import.meta.env.DEV && import.meta.env.VITE_OPEN_PROXY) ? '/proxy' : import.meta.env.VITE_APP_API_BASEURL

const api = axios.create({
  baseURL,
  timeout: 1000 * 60000,
  responseType: 'json',
})

api.baseUrl = baseURL

api.interceptors.request.use(
  (config) => {
    // 如果是重试请求（已标记 _isRetry），不重复计数
    // 如果配置了 noLoading，则跳过 loading
    if (!config._isRetry && !config.noLoading) {
      startLoading()
    }
    const userStore = useUserStore()
    /**
     * 全局拦截请求发送前提交的参数
     * 以下代码为示例，在请求头里带上 token 信息
     */
    if (isLogin() && config.headers) {
      config.headers['x-token'] = userStore.token
    }
    // 是否将 POST 请求参数进行字符串化处理
    if (config.method === 'post') {
      // config.data = qs.stringify(config.data, {
      //   arrayFormat: 'brackets',
      // })
    }
    if (config.params) {
      config.params = removeNullFields(config.params)
    }

    // 处理请求体 (POST/PUT 等)
    if (config.data) {
      config.data = removeNullFields(config.data)
    }

    return config
  },
)

// 处理错误信息的函数
function handleError(error: any) {
  if (error.code === 401) {
    useUserStore().logout()
    throw error
  }
  let message = error.message
  if (message === 'Network Error') {
    message = '后端网络故障'
  }
  else if (message.includes('timeout')) {
    message = '接口请求超时'
  }
  else if (message.includes('Request failed with status code')) {
    message = `接口${message.substr(message.length - 3)}异常`
  }
  return Promise.reject(error)
}

api.interceptors.response.use(
  (response) => {
    // 如果配置了 noLoading，则跳过关闭 loading
    if (!response.config.noLoading) {
      endLoading()
    }
    /**
     * 全局拦截请求发送后返回的数据，如果数据有报错则在这做全局的错误提示
     * 假设返回数据格式为：{ status: 1, error: '', data: '' }
     * 规则是当 status 为 1 时表示请求成功，为 0 时表示接口需要登录或者登录状态失效，需要重新登录
     * 请求出错时 error 会返回错误信息
     */
    // 下载 blob 时，接口报错可能仍返回 JSON 包在 Blob 里，需解析后再判断
    if (response.config.responseType === 'blob') {
      const blob = response.data as Blob
      const contentType = response.headers?.['content-type'] || ''
      const isJsonBlob = contentType.includes('application/json')
        || (blob.size > 0 && blob.size < 2048)
      if (isJsonBlob) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            try {
              const text = reader.result as string
              const json = JSON.parse(text) as { code?: number, message?: string }
              if (json.code !== undefined && json.code !== 200) {
                if (json.code === 401) {
                  useUserStore().logout()
                  reject(response)
                  return
                }
                if (!response.config.noErrorTip) {
                  ElMessage.success(json.message || '请求失败')
                }
                reject(response)
              }
              else {
                resolve(response)
              }
            }
            catch {
              resolve(response)
            }
          }
          reader.onerror = () => resolve(response)
          reader.readAsText(blob)
        })
      }
      return Promise.resolve(response)
    }
    if (response.data.code !== 200) {
      if (response.data.code === 401) {
        useUserStore().logout()
        return Promise.reject(response)
      }
      // 如果配置了 noErrorTip，则不弹出错误提示
      if (!response.config.noErrorTip) {
        ElMessage.success(response.data.message || '请求失败')
      }
      return Promise.reject(response)
    }
    return Promise.resolve(response.config.isResponseAll ? response : response.data)
  },
  async (error) => {
    // 获取请求配置
    const config = error.config

    // 如果配置不存在或未启用重试，则直接处理错误并关闭 loading
    if (!config || !config.retry) {
      // 如果配置了 noLoading，则跳过关闭 loading
      if (config && !config.noLoading) {
        endLoading()
      }
      return handleError(error)
    }

    // 设置重试次数
    config.retryCount = config.retryCount || 0

    // 判断是否超过重试次数
    if (config.retryCount >= MAX_RETRY_COUNT) {
      // 如果配置了 noLoading，则跳过关闭 loading
      if (!config.noLoading) {
        endLoading()
      }
      return handleError(error)
    }

    // 重试次数自增
    config.retryCount += 1

    // 标记这是重试请求，避免重复计数
    config._isRetry = true

    // 延迟重试
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))

    // 重新发起请求（标记为重试，不会再次调用 startLoading）
    return api(config)
  },
)
api.uploadFile = (url: string, formData: any, config: any = { headers: { 'Content-Type': 'multipart/form-data' } }) => {
  return api.post(url, formData, config)
}

/** 流式业务错误（如 612）：提示并写入 streamData，便于界面结束 loading */
function emitStreamBizError(streamData: any, message: string) {
  if (streamData && typeof streamData === 'object' && 'value' in streamData) {
    streamData.value = { type: 'error', error: message }
  }
}

/** 流式请求方法 */
api.stream = (url: any, data?: any, streamData?: any, config?: any) => {
  fetch(`${baseURL}${url}`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json', 'x-token': useUserStore().token },
    ...config,
  })
    .then(async (response) => {
      if (!response.ok) {
        const text = await response.text()
        let body: any
        try {
          body = text ? JSON.parse(text) : undefined
        }
        catch {
          body = undefined
        }
        const msg = body?.message || text || `请求失败(${response.status})`
        emitStreamBizError(streamData, msg)
        return
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder('utf-8')
      if (!reader) {
        throw new Error('无法读取响应体')
      }
      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          break
        }
        // 处理分块数据
        const chunk = parseJSONStream(decoder.decode(value, { stream: true }))
        for (let i = 0; i < chunk.length; i++) {
          const item = chunk[i]
          if (item && typeof item === 'object' && Number(item.code) === 612) {
            const msg = typeof item.message === 'string' ? item.message : '请求失败'
            emitStreamBizError(streamData, msg)
            try {
              await reader.cancel()
            }
            catch {
              /* ignore */
            }
            return
          }
          setTimeout(() => {
            streamData.value = item
          }, 200)
        }
      }
    })
    .catch(error => console.error('请求失败:', error))
}

function escapeControlChars(input: any) {
  let inString = false
  let result = ''
  let i = 0

  while (i < input.length) {
    const char = input[i]

    if (!inString) {
      result += char
      if (char === '"') {
        inString = true
      }
      i++
      continue
    }

    if (char === '\\') {
      result += char + input[i + 1]
      i += 2
      continue
    }

    if (char === '"') {
      inString = false
      result += char
      i++
      continue
    }

    if (char < ' ') {
      const code = char.charCodeAt(0)
      result += `\\u${code.toString(16).padStart(4, '0')}`
      i++
    }
    else {
      result += char
      i++
    }
  }

  return result
}

function parseJSONStream(input: any) {
  const escapedInput = escapeControlChars(input)
  const results = []
  let depth = 0
  let inString = false
  let escape = false
  let startIndex = 0

  for (let i = 0; i < escapedInput.length; i++) {
    const char = escapedInput[i]

    if (escape) {
      escape = false
      continue
    }

    if (char === '\\') {
      escape = true
    }

    if (char === '"') {
      inString = !inString
    }
    else if (!inString) {
      if (char === '{') {
        if (depth === 0) {
          startIndex = i
        }
        depth++
      }
      else if (char === '}') {
        depth--
        if (depth === 0) {
          try {
            results.push(JSON.parse(escapedInput.slice(startIndex, i + 1)))
          }
          catch (e: any) {
            console.error('解析错误:', e.message)
          }
        }
      }
    }
  }

  return results
}

function startLoading() {
  // 如果有延迟关闭的定时器，清除它（因为新请求来了，不应该关闭 loading）
  if (closeLoadingTimer) {
    clearTimeout(closeLoadingTimer)
    closeLoadingTimer = null
  }
  if (reqNum === 0) {
    loadingService = ElLoading.service({
      lock: true,
      fullscreen: true,
      text: '正在计算中',
      background: 'rgba(255,255,255,0.8)',
    })
  }
  reqNum++
}

/**
 * 结束 loading
 * 调用 closeLoading，它会延迟 300ms 关闭，避免频繁请求时 loading 闪烁
 */
function endLoading() {
  closeLoading()
}

/**
 * 关闭 loading 动画
 * 使用请求计数器确保所有请求完成后再关闭
 * 增加 300ms 延迟关闭，避免频繁请求时 loading 闪烁
 */
function closeLoading() {
  if (reqNum <= 0) {
    return
  }
  reqNum--
  // 当所有请求都完成时（reqNum === 0），延迟关闭 loading
  if (reqNum === 0) {
    // 清除之前的定时器（如果有）
    if (closeLoadingTimer) {
      clearTimeout(closeLoadingTimer)
      closeLoadingTimer = null
    }
    // 延迟 300ms 关闭 loading，如果在这期间有新请求，则不会关闭
    closeLoadingTimer = setTimeout(() => {
      // 再次检查 reqNum，确保在延迟期间没有新请求
      if (reqNum === 0 && loadingService) {
        loadingService.close()
        loadingService = null
      }
      closeLoadingTimer = null
    }, 300)
  }
}

export default api
